import { useState, useEffect, ChangeEvent, useRef } from 'react';
import localforage from 'localforage';
import { DomainAdd, Check, Close } from '@mui/icons-material';
import BasePaper from '@/components/ui/BasePaper';
import BaseCard from '@/components/ui/BaseCard';
import BaseTextField from '@/components/form/BaseTextField';
import BaseTitle from '@/components/ui/BaseTitle';
import BaseButton from '@/components/ui/BaseButton';
import BaseLoading from '@/components/ui/BaseLoading';
import { GridApi } from '@mui/x-data-grid';
import { getAbilitiesAPI, getRoleAPI, createRoleAPI, updateRoleAPI } from '@/api/admin/rbac';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { DialogActions, FormGroup, FormControlLabel, Checkbox, Grid2 as Grid } from '@mui/material';
import { useAppContext } from '@/provider/contexts';
import { rawStringArrayToObject } from '@/utils/permissionUtils';
import { PermissionObject } from '@/types/pages/admin/permission';
import '@/styles/admin.scss';
import { useTranslation } from 'react-i18next';

export type apiRefType = React.MutableRefObject<GridApi>;

function AdminRolePageDialog() {
    const { t } = useTranslation('common');
    const nav = useNavigate();
    const [pageLoading, setPageLoading] = useState(false);
    const [sForm, setSForm] = useState({
        role_name: '',
        role_description: '',
        role_ability: [] as number[],
    });

    const [roleNameError, setRoleNameError] = useState(false);
    const [allAbilityList, setAllAbilityList] = useState<PermissionObject>({
        page: {},
        system: [],
    });
    const fetchAbortControllerRef = useRef<AbortController | null>(null);
    const latestFetchIdRef = useRef(0);

    const { setNotification } = useAppContext();
    const { id } = useParams();
    const displayTitle = id ? t('Edit') : t('New');

    const fetchDataHandler = async () => {
        fetchAbortControllerRef.current?.abort();
        const fetchId = latestFetchIdRef.current + 1;
        latestFetchIdRef.current = fetchId;
        const abortController = new AbortController();
        fetchAbortControllerRef.current = abortController;
        setPageLoading(true);
        const notifyUnexpectedError = (error: unknown) => {
            console.error('An unexpected error occurred:', error);
            setNotification({
                message: 'An unexpected error occurred',
                severity: 'error',
            });
        };

        try {
            const corporationId = (await localforage.getItem('corporation_id')) as number;

            if (id) {
                try {
                    const {
                        data: { data },
                    } = await getRoleAPI(corporationId, Number(id), { signal: abortController.signal });
                    if (abortController.signal.aborted || latestFetchIdRef.current !== fetchId) return;

                    const formData = {
                        role_name: data.role_name,
                        role_description: data.role_description ?? '',
                        role_ability: data.role_ability
                            ? data.role_ability.map((e) => {
                                  return e.ability_id;
                              })
                            : [],
                    };
                    setSForm(formData);
                } catch (error) {
                    if (abortController.signal.aborted || latestFetchIdRef.current !== fetchId) return;
                    notifyUnexpectedError(error);
                }
            }

            try {
                const {
                    data: { data },
                } = await getAbilitiesAPI({ signal: abortController.signal });
                if (abortController.signal.aborted || latestFetchIdRef.current !== fetchId) return;
                const abilityObj = rawStringArrayToObject(data.aaData);
                setAllAbilityList(abilityObj);
            } catch (error) {
                if (abortController.signal.aborted || latestFetchIdRef.current !== fetchId) return;
                notifyUnexpectedError(error);
            }
        } catch (error) {
            if (abortController.signal.aborted) return;
            notifyUnexpectedError(error);
        } finally {
            if (latestFetchIdRef.current === fetchId) {
                setPageLoading(false);
                fetchAbortControllerRef.current = null;
            }
        }
    };

    useEffect(() => {
        fetchDataHandler();

        return () => {
            fetchAbortControllerRef.current?.abort();
        };
    }, []);

    const handleInputForm = (f: string, e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = e.target.value;
        if (f === 'role_name') {
            setRoleNameError(value.length ? false : true);
        }
        setSForm((prevState) => ({
            ...prevState,
            [f]: value,
        }));
    };

    const handleCheckbox = (e: ChangeEvent<HTMLInputElement>) => {
        const checked = e.target.checked;
        const value = e.target.value;

        let newRoleAbility = [...sForm.role_ability];

        if (checked) {
            newRoleAbility.push(Number(value));
        } else {
            newRoleAbility = newRoleAbility.filter((id) => id !== Number(value));
        }

        setSForm((prevState) => ({
            ...prevState,
            role_ability: newRoleAbility,
        }));
    };

    const formAbilitiesPage = () => {
        const actionList = ['read', 'write', 'delete']; //Basic required
        const actionLabel = (key: string) =>
            ({
                read: t('read'),
                write: t('write'),
                delete: t('delete'),
            })[key] ?? t(key);
        const distinctActions = [
            ...new Set(
                Object.values(allAbilityList.page)
                    .flatMap((item) => item.actions)
                    .map((action) => action.action),
            ),
        ];

        const finalActionList = [...new Set([...actionList, ...distinctActions])]; //Just in case have new type of action in the future
        const output: React.ReactElement[] = [];

        output.push(
            <Grid container spacing={2} key={'rowHeader'}>
                <Grid size={3}></Grid>
                {finalActionList.map((e) => (
                    <Grid size={1} key={e} sx={{ textTransform: 'uppercase' }}>
                        {actionLabel(e)}
                    </Grid>
                ))}
            </Grid>,
        );

        const hasStoreAllPermission = sForm.role_ability.includes(2); // "StoreAll" id value is 2

        if (!hasStoreAllPermission) {
            removeStoreFeaturePages();
        }

        const sortedPages = Object.entries(allAbilityList.page).sort(([, a], [, b]) => {
            const idxA = a.actions.find((x) => x.action === 'read')?.order_idx ?? 0;
            const idxB = b.actions.find((x) => x.action === 'read')?.order_idx ?? 0;
            return idxA - idxB;
        });

        for (const [pageKey, page] of sortedPages) {
            const actionElementList: React.ReactElement[] = [];

            for (const rsAction of finalActionList) {
                const act = page.actions.find((ra) => ra.action === rsAction);
                const checked = act ? sForm.role_ability.includes(act.id!) : false;

                actionElementList.push(
                    <Grid size={1} key={`${pageKey}_${rsAction}`}>
                        {act && (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={checked}
                                        onChange={handleCheckbox}
                                        value={act.id}
                                        disabled={!checkStoreFeaturePageEnabled(hasStoreAllPermission, pageKey)}
                                    />
                                }
                                label=""
                            />
                        )}
                    </Grid>,
                );
            }

            output.push(
                <Grid container spacing={2} key={pageKey}>
                    <Grid size={3} sx={{ display: 'flex', alignItems: 'center' }}>
                        {page.name}
                    </Grid>
                    {actionElementList}
                </Grid>,
            );
        }

        return output;
    };

    const checkStoreFeaturePageEnabled = (hasStoreAllPermission: boolean, pageKey: string) => {
        const keyName = pageKey.split('-')[1];
        const isStoreFeaturePage = keyName.includes('store') && !keyName.includes('stores');
        if (isStoreFeaturePage) {
            return hasStoreAllPermission;
        } else {
            return true;
        }
    };

    const removeStoreFeaturePages = () => {
        for (const [key, value] of Object.entries(allAbilityList.page)) {
            const keyName = key.split('-')[1];
            if (keyName.includes('store') && !keyName.includes('stores')) {
                value.actions.forEach((action) => {
                    sForm.role_ability = sForm.role_ability.filter((id) => id !== action.id);
                });
            }
        }
    };

    const handleSave = async () => {
        if (!sForm.role_name.length) {
            setRoleNameError(true);
            return;
        }

        try {
            const corporationId = (await localforage.getItem('corporation_id')) as number;

            const rs = id
                ? await updateRoleAPI(corporationId, Number(id), sForm)
                : await createRoleAPI(corporationId, sForm);

            if (rs.data?.status === 200) {
                setNotification({
                    message: id ? t('Update successful!') : t('Create successful!'),
                    severity: 'success',
                });

                nav('/admin/role');
            } else {
                setNotification({
                    message: rs.data?.message || (id ? t('Update failed!') : t('Create failed!')),
                    severity: 'error',
                    duration: 8000,
                });
            }
        } catch (error) {
            console.error('An unexpected error occurred:', error);
            setNotification({
                message: t('An unexpected error occurred'),
                severity: 'error',
            });
        }
    };

    return (
        <>
            <BasePaper className={'admin-paper'}>
                <BaseTitle icon={<DomainAdd />} title={`${displayTitle} ${t('Role')}`} />

                <BaseCard>
                    <BaseLoading open={pageLoading} />

                    <div className="card-header">
                        <BaseTextField
                            id="role_name"
                            error={roleNameError}
                            helperText={roleNameError ? t('Required') : ''}
                            placeholder={t('Role Name')}
                            sx={{ my: 2, width: 300 }}
                            value={sForm.role_name}
                            onChange={(e) => handleInputForm('role_name', e)}
                        />
                        <BaseTextField
                            id="role_description"
                            placeholder={t('Role Description')}
                            sx={{ my: 2, width: 500 }}
                            value={sForm.role_description}
                            onChange={(e) => handleInputForm('role_description', e)}
                        />
                        <div style={{ width: 100 }}></div>
                    </div>
                </BaseCard>

                <BaseCard>
                    <h2>{t('Permissions / Page')}</h2>
                    <FormGroup>{formAbilitiesPage()}</FormGroup>

                    <DialogActions>
                        <BaseButton
                            variant="contained"
                            startIcon={<Check />}
                            style={{
                                backgroundColor: 'var(--confirm-btn-color)',
                                color: '#fff',
                                fontWeight: 'bold',
                            }}
                            onClick={() => handleSave()}
                        >
                            {t('Save')}
                        </BaseButton>
                        <Link to="/admin/role">
                            <BaseButton
                                variant="outlined"
                                startIcon={<Close />}
                                style={{
                                    borderColor: 'var(--cancel-btn-color)',
                                    color: 'var(--cancel-btn-color)',
                                    fontWeight: 'bold',
                                }}
                            >
                                {t('Cancel')}
                            </BaseButton>
                        </Link>
                    </DialogActions>
                </BaseCard>
            </BasePaper>
        </>
    );
}

export default AdminRolePageDialog;
